import { compareStrings } from '@shure/shared/angular/ui/comparators/string';

export type ComparatorFn<TData> = (
	a: TData | string | null | undefined,
	b: TData | string | null | undefined
) => number;

/**
 * AG Grid comparators for custom rendered columns will be called in two contexts :
 *   1. The user clicks the column header to sort - in this case, the comparator is called with cell data objects
 *   2. The user opens the column filter menu - in this case, the comparator is called with a string (the return
 *      value of the filterValueGetter function)
 *
 * This function function makes it easier to setup a comparator that will call the correct compare function
 * based on the data type of the args.
 *
 * @example
 * ```
 * comparator: makeComparator<MyCellDataType>((a, b) => compareCells(a, b))
 * ```
 */
export function makeComparator<TData>(
	compareObjects: (a: TData | undefined, b: TData | undefined) => number
): ComparatorFn<TData> {
	return (a, b) =>
		isStringComparable(a) && isStringComparable(b) ? compareStrings(a, b) : compareObjects(<TData>a, <TData>b);
}

function isStringComparable(value: unknown): value is string | null | undefined {
	return typeof value === 'string' || value === null || value === undefined;
}
